import RytmWebflow from 'rytm-webflow'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { trace } from 'rytm-helpers'
import { WCAG } from "rytm-wcag"
import { windowUpdate, removeReCaptchaBadge } from './shared/common'
import { initHeaderNavBrand } from './shared/headerNavBrand'
import { initHeaderMobile } from './shared/headerMobile'
import { initMovieFooter, destroyMovieFooter } from './shared/movieFooter'
import { initBannerRotator } from './shared/bannerRotator'
import { restoreListingScroll } from './shared/listings'
import { updateColorTokens } from './shared/tokens'
import { navLoadingStart, navLoadingEnd } from './shared/navLoading'
// controllers
import Controller from './controllers/Controller'
// views
import TypeRevealView from './views/TypeRevealView'

const routes = {
  'webflow': {
    controller: Controller,
    view: RytmWebflow.WebflowView
  },
  'list': {
    controller: Controller,
    view: RytmWebflow.WebflowListView
  },
  'typereveal': {
    controller: Controller,
    view: TypeRevealView
  },
}
const aswapProps = {
  animationTimeHide: 800,
  animationTimeShow: 800,
  preloadOnHover: false,
  cacheOn: true,
  refreshOnSameUrl: false,
  routes,
  trace,
}
// ASwap fires `onViewSwapComplete` on the initial view too (~1.8s in, after the
// loading curtain has lifted). That first firing's banner rotation is already done
// synchronously in app.ts (under the curtain, no flash), so skip it here — only
// real navigations rotate from the hook. Avoids double-advancing the cursor.
let bannerInitialSwapHandled = false

const aswapEvents = {
  onRequestLoad: () => {
    // Network-bound navigation request just went out (cached navigations skip
    // this event). Arm the delayed loading indicator — it only reveals if the
    // request outlives the grace period; fast loads stay silent.
    navLoadingStart()
  },
  onRequestLoaded: () => {
    // Response received — cancel a pending reveal (fast case) or ease the
    // indicator back out (slow case). Fires before the view hide/swap, so the
    // spinner leaves as the incoming content begins swapping in.
    navLoadingEnd()
  },
  onViewSwapComplete: () => {
    WCAG.dialogCloseAll()
    windowUpdate()
    // Re-apply the relative brand color from the swapped-in `.body-tokens` carrier
    // (the <head> script only runs on full loads). No-op unless it changed.
    updateColorTokens()
    // Rebuild the header scroll triggers against the swapped-in DOM (nav-brand
    // measures the brand link width and binds to header elements at init; the
    // mobile collapse re-arms its matchMedia trigger), then recompute all
    // scroll-trigger positions now that the page height has changed.
    initHeaderNavBrand()
    initHeaderMobile()
    // Re-arm the movie footer's pinned horizontal carousel against the
    // swapped-in DOM before recomputing scroll-trigger positions.
    initMovieFooter()
    // Advance the ad-banner rotation for the swapped-in page (its banner set is
    // baked into the HTML). Skip the initial view (handled in app.ts); rotate on
    // every real navigation. Before refresh() so the revealed slide is measured.
    if (bannerInitialSwapHandled) {
      initBannerRotator()
    } else {
      bannerInitialSwapHandled = true
    }
    ScrollTrigger.refresh()
    // Restore scroll + focus if we just navigated back to a listing (no-op
    // otherwise). Self-defers past ASwap's own scroll-to-top, so it lands last.
    restoreListingScroll()
  },
  onViewHide: () => {
    WCAG.dialogCloseAll()
    // Navigation started — close any open offcanvas (mobile menu / live feed)
    // so it doesn't linger over the incoming page. Also releases the scroll
    // lock the store applied on open. Semi-SPA: the page never reloads, so
    // nothing else would reset this.
    ;(window as any).Alpine?.store('offcanvas')?.close()
    // Tear down the movie footer pin (and its pin-spacer) before the DOM swaps.
    destroyMovieFooter()
    removeReCaptchaBadge()
  },
  onViewHidden: () => {
    // destroy...
  },
  onRequestSameUrl: () => {
    // same url request
  },
}

export const initASwap = () => {
  RytmWebflow.aswap.init(aswapProps, aswapEvents)
}